// ---------------------------------------------------------------------------
// Redaction — nothing private leaves the device inside a crash report
//
// Diagnostics text is meant to be copied into chats and bug trackers, so it has
// to be safe to paste. Anything that looks like a key, a token, a bearer
// header, an email address, a signed storage URL or a long random string is
// replaced with a marker before the report is shown, copied or exported.
// ---------------------------------------------------------------------------

const RULES: Array<[RegExp, string]> = [
  // Bearer / basic auth headers
  [/\b(bearer|basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi, "$1 [redacted]"],
  // JWTs
  [/\beyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{4,}\b/g, "[redacted-jwt]"],
  // Known key prefixes (Supabase, Stripe, OpenAI, GitHub, Google, Slack…)
  [/\b(sb_(?:publishable|secret)|sk|pk|rk|ghp|gho|ghs|ghu|github_pat|xox[abprs]|AIza|AKIA|ASIA)[-_A-Za-z0-9]{8,}/g, "[redacted-key]"],
  // key/token/secret/password assignments in any shape
  [/((?:api[_-]?key|access[_-]?token|refresh[_-]?token|id[_-]?token|token|secret|password|passwd|pwd|auth|apikey|signature|sig)["']?\s*[:=]\s*["']?)([^"'\s,&}]{6,})/gi, "$1[redacted]"],
  // Signed URL query params
  [/([?&](?:token|signature|sig|key|apikey|access_token|X-Amz-Signature|X-Goog-Signature)=)[^&\s"']+/gi, "$1[redacted]"],
  // Email addresses
  [/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, "[redacted-email]"],
  // Card-like digit runs
  [/\b(?:\d[ -]?){13,19}\b/g, "[redacted-number]"],
];

/** Make a single string safe to paste. */
export function redactText(input: string): string {
  let out = input;
  for (const [re, replacement] of RULES) {
    try { out = out.replace(re, replacement); } catch { /* never break the report */ }
  }
  return out;
}

const SENSITIVE_KEY = /(key|token|secret|password|passwd|pwd|auth|cookie|session|signature|credential|email)/i;

/** Deep-clean any JSON-ish value: sensitive keys dropped, strings scrubbed. */
export function redactValue<T>(value: T, depth = 0): T {
  if (depth > 8) return "[redacted-deep]" as unknown as T;
  if (typeof value === "string") return redactText(value) as unknown as T;
  if (Array.isArray(value)) return value.map((v) => redactValue(v, depth + 1)) as unknown as T;
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEY.test(k) ? "[redacted]" : redactValue(v, depth + 1);
    }
    return out as unknown as T;
  }
  return value;
}
